// src/components/WeeklySurvey.js  (/weekly-survey)
//
// The coordinator weekly report, patient by patient.
//
//   1. Pick the report period (an ISO week, cut at month ends: reportPeriods.js).
//   2. Add a study. In it: add people (search RegX as you type, or someone
//      who isn't in RegX; DOB + MRN fill in; ONE status of five; a subject
//      number is required for Enrolled/Randomized) and/or a number of
//      follow-ups not listed by name.
//   3. Pre-screens not tied to a study: typed in the status card at the top
//      (StatusOverview), which also shows every status's running total.
//   4. "Add another study?" until done, then review the numbers (filled in
//      from the patients, read-only) and submit.
//
// Everything is autosaved to a per-user draft (weekly_report_drafts/{uid}) as
// it changes, so a closed pop-up, a reload or leaving the page loses nothing.
// Leaving warns that the report isn't submitted yet.
//
// A coordinator always reports as themselves (matched by login email on the
// coordinator roster). Admins may pick any coordinator to file on their behalf.
//
// On submit the counts go to weekly_reports exactly as the old form's numbers
// did (so Admin Reports and /statistics are unchanged) and the patient list to
// weekly_report_patients (admins + that coordinator only). Patient records are
// never written. Rules: services/weeklyReportModel.js.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { backTarget } from "../utils/backTarget";
import { collection, getDocs } from "firebase/firestore";
import { db, auth } from "../firebase";
import { useTenant } from "../context/TenantContext";
import { fetchActiveCoordinators } from "../services/coordinatorService";
import { getStudiesPath } from "../services/firestorePaths";
import { loadReportSearchPatients } from "../services/reportSearchPatients";
import { buildSearchIndex } from "../services/reportPatientSearch";
import { segmentOptions, segmentByKey } from "../services/reportPeriods";
import {
  emptyDraft,
  draftIsEmpty,
  draftHasContent,
  newEntryKey,
  validateEntry,
} from "../services/weeklyReportModel";
import { suggestionsFor } from "../services/reportSuggestions";
import {
  loadDraft,
  saveDraft,
  deleteDraft,
  submitReport,
  loadMyReportPatients,
  loadReportsForCoordinator,
} from "../services/weeklyReportService";
import SearchableSelect from "./SearchableSelect";
import AddPatientModal, { blankEntry } from "./weeklyReport/AddPatientModal";
import AddToRegxModal from "./weeklyReport/AddToRegxModal";
import { normalizePatientForMatching } from "../services/eligibilityMatch";
import { statusLabel } from "../constants/diseaseOptions";
import {
  StudyPickerModal,
  StudyPatientsModal,
  AnotherStudyModal,
  TotalsChips,
} from "./weeklyReport/StudyModals";
import { ReviewSubmitModal, LeaveGuardModal } from "./weeklyReport/ReviewSubmitModal";
import ReportsGrid from "./weeklyReport/ReportsGrid";
import StatusOverview from "./weeklyReport/StatusOverview";
import { BTN_PRIMARY, BTN_SMALL, INPUT, LABEL } from "./weeklyReport/ui";
import {
  ArrowLeft,
  ClipboardList,
  FlaskConical,
  Plus,
  Trash2,
  Send,
  CheckCircle2,
  History,
  Loader2,
  AlertTriangle,
} from "lucide-react";

// Study STATUS is free text and has been saved as "Active" as well as
// "ACTIVE"; compare case-insensitively so no active study goes missing.
const statusOf = (s) => String(s?.STATUS || "").trim().toUpperCase();
const isActiveStatus = (s) => statusOf(s) === "ACTIVE" || statusOf(s) === "ACTIVE/NR";

const fmtTime = (ms) =>
  ms ? new Date(ms).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";

const WeeklySurvey = () => {
  const navigate = useNavigate();
  // After submitting: back to the home page when opened from there, else the Coordinator page.
  const afterSubmit = backTarget(useLocation().state, "/coordinator", "Coordinator page");
  const { user, isOwner, isTenantAdmin, roleForActiveSite, tenantId } = useTenant();
  const isAdmin = !!(isOwner || isTenantAdmin?.(tenantId) || roleForActiveSite === "admin");
  const uid = user?.uid || auth.currentUser?.uid || "";
  const myEmail = String(user?.email || auth.currentUser?.email || "").toLowerCase();

  // ---------------- Coordinator roster ----------------
  const [coordinators, setCoordinators] = useState([]);
  const [coordinatorsLoaded, setCoordinatorsLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetchActiveCoordinators()
      .then((list) => !cancelled && setCoordinators((list || []).filter((c) => String(c.name || "").trim())))
      .catch((err) => console.warn("Could not load coordinators:", err))
      .finally(() => !cancelled && setCoordinatorsLoaded(true));
    return () => {
      cancelled = true;
    };
  }, []);
  const myCoordinator = useMemo(
    () => coordinators.find((c) => String(c.email || "").toLowerCase() === myEmail) || null,
    [coordinators, myEmail]
  );

  // ---------------- Studies ----------------
  const [studies, setStudies] = useState([]);
  useEffect(() => {
    getDocs(collection(db, getStudiesPath()))
      .then((snap) => setStudies(snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter(isActiveStatus)))
      .catch((err) => console.error("❌ Could not load studies:", err));
  }, []);

  // ---------------- Patients (for the search) ----------------
  // The CRC page's patients only (no potential duplicates): reportSearchPatients.js
  const [patientsState, setPatientsState] = useState({ loading: true, sheetError: "", index: [] });
  useEffect(() => {
    let cancelled = false;
    loadReportSearchPatients()
      .then(({ patients, sheetError }) => {
        if (!cancelled) setPatientsState({ loading: false, sheetError, index: buildSearchIndex(patients) });
      })
      .catch((err) => {
        console.error("❌ Could not load patients for the search:", err);
        if (!cancelled)
          setPatientsState({ loading: false, sheetError: "The patient list couldn't be loaded.", index: [] });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // ---------------- Draft (autosaved) ----------------
  const [draft, setDraft] = useState(emptyDraft);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [restoredAt, setRestoredAt] = useState(0);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error
  const hadDraftRef = useRef(false); // a draft doc exists that may need deleting
  const saveTimer = useRef(null);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!uid) return;
    let cancelled = false;
    loadDraft(uid)
      .then((res) => {
        if (cancelled || !res) return;
        hadDraftRef.current = true;
        if (!draftIsEmpty(res.draft)) {
          setDraft(res.draft);
          setRestoredAt(res.updatedAtMs || Date.now());
        }
      })
      .catch((err) => console.error("❌ Could not load your saved draft:", err))
      .finally(() => !cancelled && setDraftLoaded(true));
    return () => {
      cancelled = true;
    };
  }, [uid]);

  // The newest draft not yet written, so leaving the page mid-delay can flush it.
  const unsavedRef = useRef(null);
  useEffect(() => {
    if (!draftLoaded || !uid) return undefined;
    const empty = draftIsEmpty(draft);
    if (empty && !hadDraftRef.current) return undefined;
    setSaveState("saving");
    unsavedRef.current = empty ? null : draft;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      if (submittingRef.current) return;
      unsavedRef.current = null;
      try {
        if (empty) await deleteDraft(uid);
        else await saveDraft(uid, draft);
        hadDraftRef.current = !empty;
        setSaveState(empty ? "idle" : "saved");
      } catch (err) {
        console.error("❌ Could not save your draft:", err);
        setSaveState("error");
      }
    }, 700);
    return () => clearTimeout(saveTimer.current);
  }, [draft, draftLoaded, uid]);

  // Leaving the page (in-app navigation, tab hidden or closed) writes any
  // change still waiting out the delay above.
  useEffect(() => {
    const flush = () => {
      const pending = unsavedRef.current;
      if (!pending || !uid || submittingRef.current) return;
      unsavedRef.current = null;
      saveDraft(uid, pending).catch((err) => console.error("❌ Could not save your draft:", err));
    };
    const onHide = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onHide);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onHide);
      flush(); // unmount: navigating elsewhere in the app
    };
  }, [uid]);

  const hasWork = !draftIsEmpty(draft);
  // Closing or reloading the tab with an unsubmitted report asks first.
  useEffect(() => {
    if (!hasWork) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [hasWork]);

  // ---------------- Coordinator for this report ----------------
  // Admins pick (prefilled with their own name when they're on the roster).
  useEffect(() => {
    if (isAdmin && coordinatorsLoaded && !draft.coordinator && myCoordinator?.name) {
      setDraft((d) => (d.coordinator ? d : { ...d, coordinator: myCoordinator.name }));
    }
  }, [isAdmin, coordinatorsLoaded, myCoordinator, draft.coordinator]);

  const coordinatorName = isAdmin ? draft.coordinator : myCoordinator?.name || "";
  const coordinatorEmail = isAdmin
    ? String(coordinators.find((c) => c.name === draft.coordinator)?.email || "").toLowerCase()
    : myEmail;

  // ---------------- Past reports ----------------
  // A coordinator sees their own (filed by them, or by an admin for their
  // email). An admin sees the reports FOR the coordinator picked above, whoever
  // filed them, so a report filed by the coordinator and one filed on their
  // behalf are in one list.
  const pastFor = isAdmin ? draft.coordinator : "";
  const [mine, setMine] = useState({ loading: true, error: "", reports: [], approvals: {} });
  const loadMine = useCallback(async () => {
    if (!uid) return;
    if (isAdmin && !pastFor) {
      setMine({ loading: false, error: "", reports: [], approvals: {} });
      return;
    }
    setMine((m) => ({ ...m, loading: true }));
    try {
      const reports = isAdmin
        ? await loadReportsForCoordinator({ name: pastFor, email: coordinatorEmail })
        : await loadMyReportPatients({ uid, email: myEmail });
      setMine({ loading: false, error: "", reports, approvals: {} });
    } catch (err) {
      console.error("❌ Could not load past reports:", err);
      setMine({ loading: false, error: "Past reports couldn't be loaded.", reports: [], approvals: {} });
    }
  }, [uid, myEmail, isAdmin, pastFor, coordinatorEmail]);
  useEffect(() => {
    loadMine();
  }, [loadMine]);

  // ---------------- Period ----------------
  const periodChoices = useMemo(() => {
    const base = segmentOptions(new Date());
    if (draft.segmentKey && !base.some((o) => o.key === draft.segmentKey)) {
      const kept = segmentByKey(draft.segmentKey);
      if (kept) base.push(kept);
    }
    return base;
  }, [draft.segmentKey]);
  const segment = periodChoices.find((o) => o.key === draft.segmentKey) || periodChoices[0];

  // ---------------- Draft edits ----------------
  const studyOf = (id) => draft.studies.find((s) => s.studyDocId === id) || null;
  const addStudy = (s) =>
    setDraft((d) =>
      d.studies.some((x) => x.studyDocId === s.id)
        ? d
        : {
            ...d,
            studies: [
              ...d.studies,
              { studyDocId: s.id, studyId: s.STUDY_ID || "", name: s.NAME || "", patients: [], followUpCount: "" },
            ],
          }
    );
  const removeStudy = (id) =>
    setDraft((d) => ({
      ...d,
      studies: d.studies.filter((s) => s.studyDocId !== id),
      pending: d.pending?.studyDocId === id ? null : d.pending,
    }));
  const setPending = (studyDocId, entry, editing = false) =>
    setDraft((d) => ({ ...d, pending: { studyDocId, entry, editing } }));
  const clearPending = () => setDraft((d) => ({ ...d, pending: null }));
  const upsertPatient = (studyDocId, entry) =>
    setDraft((d) => ({
      ...d,
      pending: null,
      studies: d.studies.map((s) =>
        s.studyDocId !== studyDocId
          ? s
          : {
              ...s,
              patients: s.patients.some((p) => p.key === entry.key)
                ? s.patients.map((p) => (p.key === entry.key ? entry : p))
                : [...s.patients, entry],
            }
      ),
    }));
  const removePatient = (studyDocId, key) =>
    setDraft((d) => ({
      ...d,
      studies: d.studies.map((s) =>
        s.studyDocId === studyDocId ? { ...s, patients: s.patients.filter((p) => p.key !== key) } : s
      ),
    }));

  // ---------------- Pop-up flow ----------------
  // { kind: "studyPicker" | "studyPatients" | "addPatient" | "another" | "review" | "leave", studyDocId }
  const [modal, setModal] = useState(null);
  const [regxNotice, setRegxNotice] = useState(""); // "Added to RegX in …", shown on the patient

  const openAddPatient = (studyDocId) => {
    setRegxNotice("");
    const p = draft.pending;
    if (p && p.studyDocId === studyDocId && !p.editing) {
      setModal({ kind: "addPatient", studyDocId }); // continue the half-filled one
      return;
    }
    if (p && p.studyDocId !== studyDocId && (p.entry?.patientRef || p.entry?.notInRegx || p.entry?.status)) {
      const other = studyOf(p.studyDocId);
      if (!window.confirm(`You have an unfinished patient for ${other?.name || "another study"}. Discard it?`)) return;
    }
    setPending(studyDocId, blankEntry(), false);
    setModal({ kind: "addPatient", studyDocId });
  };

  // Picking a study opens it, offering both: add a person, or type a
  // follow-up number.
  const onPickStudy = (s) => {
    addStudy(s);
    setModal({ kind: "studyPatients", studyDocId: s.id });
  };
  const setFollowUpCount = (studyDocId, value) =>
    setDraft((d) => ({
      ...d,
      studies: d.studies.map((s) => (s.studyDocId === studyDocId ? { ...s, followUpCount: value } : s)),
    }));

  const onQuickAdd = (studyDocId, suggestion, status) => {
    const entry = { key: newEntryKey(), ...suggestion.entry, status };
    // Enrolled/Randomized needs a subject number: open the form, filled in.
    if (status === "enrolled" && !String(entry.subjectNumber || "").trim()) {
      setPending(studyDocId, entry, false);
      setModal({ kind: "addPatient", studyDocId });
      return;
    }
    const errors = validateEntry(entry, studyOf(studyDocId)?.patients || []);
    if (errors.length) {
      alert(errors.join("\n"));
      return;
    }
    upsertPatient(studyDocId, entry);
  };

  const modalStudy = modal?.studyDocId ? studyOf(modal.studyDocId) : null;
  const suggestions = useMemo(
    () =>
      modalStudy
        ? suggestionsFor({
            priorDocs: mine.reports,
            studyDocId: modalStudy.studyDocId,
            currentPatients: modalStudy.patients,
          })
        : [],
    [modalStudy, mine.reports]
  );

  // ---------------- Submit ----------------
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submittedNote, setSubmittedNote] = useState("");
  const blockedReason = !coordinatorsLoaded
    ? "Loading coordinators…"
    : !coordinatorName
    ? isAdmin
      ? "Pick the coordinator this report is for."
      : "Your login email isn't linked to a coordinator name, so this report can't be filed yet. Ask an admin to add your email on Manage Coordinators."
    : !draftHasContent(draft)
    ? "Add at least one patient, or a pre-screen or follow-up number."
    : "";
  const canSubmit = !blockedReason && !!segment && !!uid;

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    submittingRef.current = true;
    clearTimeout(saveTimer.current);
    unsavedRef.current = null;
    setSubmitError("");
    try {
      await submitReport({
        uid,
        authorEmail: myEmail,
        draft,
        segment,
        coordinator: coordinatorName,
        coordinatorEmail,
      });
      // The submit deleted the draft doc in the same batch.
      hadDraftRef.current = false;
      setDraft((d) => ({ ...emptyDraft(), coordinator: d.coordinator }));
      setRestoredAt(0);
      setSaveState("idle");
      setModal(null);
      setSubmittedNote(`Report for ${segment.label} submitted. Your admin can see it now.`);
      loadMine();
    } catch (err) {
      console.error("❌ Weekly report submit failed:", err);
      setSubmitError("Couldn't submit. Your report is still saved as a draft; please try again.");
      // Make sure the draft holds the latest edits (the pending save was cancelled).
      saveDraft(uid, draft)
        .then(() => setSaveState("saved"))
        .catch(() => setSaveState("error"));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (hasWork) setModal({ kind: "leave" });
    else navigate(-1);
  };

  const startOver = () => {
    if (!window.confirm("Clear this report and start again? This can't be undone.")) return;
    setDraft((d) => ({ ...emptyDraft(), coordinator: d.coordinator }));
    setRestoredAt(0);
  };

  const saveText =
    saveState === "saving"
      ? "Saving draft…"
      : saveState === "saved"
      ? "Draft saved"
      : saveState === "error"
      ? "Couldn't save the draft. Check your connection."
      : "";

  const pendingStudy = draft.pending ? studyOf(draft.pending.studyDocId) : null;
  const studyIdsInReport = draft.studies.map((s) => s.studyDocId);

  // ---------------- UI ----------------
  return (
    <div className="relative min-h-screen w-full text-slate-100 font-[Manrope]">
      {/* Background layer (flips in light mode) */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <img src="/bg_ai.jpg" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#070418]/90 via-[#0c0726]/85 to-[#04030f]/95" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.25),_transparent_55%)]" />
        <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-blue-600/20 blur-[140px]" />
        <div className="absolute -bottom-32 -left-24 w-[460px] h-[460px] rounded-full bg-purple-600/20 blur-[140px]" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 py-6 pb-28">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-sm text-slate-200 transition"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-purple-300 shrink-0" />
              <h1 className="text-lg font-semibold tracking-tight text-white truncate">Weekly Coordinator Report</h1>
            </div>
            <p className="text-[11px] text-slate-400">Add the patients you worked with on each study</p>
          </div>
        </div>

        {submittedNote && (
          <div className="weekly-report-ok mb-4 flex items-start gap-2 rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">
            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
            <span className="flex-1">{submittedNote}</span>
            <button type="button" className={BTN_SMALL} onClick={() => navigate(afterSubmit.to)}>
              Back to {afterSubmit.label}
            </button>
          </div>
        )}

        {restoredAt > 0 && hasWork && (
          <div className="weekly-report-warn mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
            <History className="w-4 h-4 shrink-0" />
            <span className="flex-1 min-w-0">
              We restored the report you started{restoredAt ? ` (last saved ${fmtTime(restoredAt)})` : ""}.
            </span>
            <button type="button" className={BTN_SMALL} onClick={startOver}>
              Start over
            </button>
          </div>
        )}

        {/* Who + when */}
        <div className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl overflow-hidden mb-5">
          <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-purple-400 via-fuchsia-400 to-blue-400 opacity-70 pointer-events-none" />
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <span className={LABEL}>Coordinator</span>
              {isAdmin ? (
                coordinatorsLoaded && coordinators.length === 0 ? (
                  <input
                    placeholder="First name only, capitalized (e.g. Aki)"
                    value={draft.coordinator}
                    onChange={(e) => setDraft((d) => ({ ...d, coordinator: e.target.value }))}
                    className={INPUT}
                  />
                ) : (
                  <SearchableSelect
                    value={draft.coordinator}
                    options={coordinators.map((c) => c.name)}
                    onChange={(name) => setDraft((d) => ({ ...d, coordinator: name }))}
                    loading={!coordinatorsLoaded}
                    placeholder="Who is this report for?"
                    noMatchText="No coordinator matches that."
                    inputClassName={`${INPUT} pl-9 pr-9 disabled:opacity-60`}
                  />
                )
              ) : (
                <div className={`${INPUT} flex items-center min-h-[38px]`} aria-readonly="true">
                  {!coordinatorsLoaded ? (
                    <span className="text-slate-400">Loading…</span>
                  ) : myCoordinator ? (
                    myCoordinator.name
                  ) : (
                    <span className="text-rose-300">Not linked to a coordinator</span>
                  )}
                </div>
              )}
              {isAdmin && (
                <p className="mt-1 text-[10px] text-slate-400">As an admin you can file on a coordinator's behalf.</p>
              )}
              {isAdmin && draft.coordinator && coordinatorsLoaded && !coordinatorEmail && (
                <p className="mt-1 text-[11px] text-amber-300">
                  {draft.coordinator} has no email on the coordinator list, so they won't see reports you file for them.
                  Add it on Manage Coordinators.
                </p>
              )}
              {!isAdmin && coordinatorsLoaded && !myCoordinator && (
                <p className="mt-1 text-[11px] text-rose-300">
                  Your login email isn't on the coordinator list. Ask an admin to add it on Manage Coordinators. You can
                  still fill in the report; it's saved as a draft.
                </p>
              )}
            </div>
            <div>
              <span className={LABEL}>Report period</span>
              <select
                value={segment?.key || ""}
                onChange={(e) => setDraft((d) => ({ ...d, segmentKey: e.target.value }))}
                className={INPUT}
              >
                {periodChoices.map((o) => (
                  <option key={o.key} value={o.key} className="bg-[#0b0820]">
                    {o.label} ({o.monthLabel})
                  </option>
                ))}
              </select>
              {segment?.partOfSplitWeek && (
                <p className="mt-1 text-[11px] text-slate-400">
                  This week runs into another month, so each month's days are reported separately (goals restart on the
                  1st).
                </p>
              )}
            </div>
          </div>
        </div>

        {/* One card: every status, its total so far, and where it's entered */}
        <StatusOverview
          studies={draft.studies}
          preScreensTyped={draft.counts?.preScreens ?? ""}
          onPreScreensChange={(v) => setDraft((d) => ({ ...d, counts: { ...(d.counts || {}), preScreens: v } }))}
          onPickStudy={() => setModal({ kind: "studyPicker" })}
        />

        {/* Unfinished patient */}
        {draft.pending && pendingStudy && modal?.kind !== "addPatient" && modal?.kind !== "addToRegx" && (draft.pending.entry?.patientRef || draft.pending.entry?.notInRegx || draft.pending.entry?.status) && (
          <div className="weekly-report-warn mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span className="flex-1 min-w-0">You were {draft.pending.editing ? "editing" : "adding"} a patient for {pendingStudy.name || pendingStudy.studyId}.</span>
            <button type="button" className={BTN_SMALL} onClick={() => setModal({ kind: "addPatient", studyDocId: pendingStudy.studyDocId })}>
              Continue
            </button>
          </div>
        )}

        {/* The main step, big and in the middle so it's clear what to click
            (2026-10-09): add a study, then the people in it. */}
        <div className="flex justify-center mb-5">
          <button
            type="button"
            className={`${BTN_PRIMARY} !px-8 !py-3 !text-base`}
            onClick={() => setModal({ kind: "studyPicker" })}
          >
            <Plus className="w-5 h-5" />
            Add a study
          </button>
        </div>

        {/* Studies in this report */}
        <div className="flex items-center gap-2 mb-3 px-1">
          <FlaskConical className="w-3.5 h-3.5 text-purple-300" />
          <h2 className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white">Studies in this report</h2>
          <span className="h-px flex-1 bg-white/5" />
        </div>

        {draft.studies.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] px-4 py-8 text-center text-sm text-slate-400 mb-5">
            No studies yet. Click "Add a study" above, then add the people you worked with and/or a number of follow-ups.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
            {draft.studies.map((s) => (
              <div
                key={s.studyDocId}
                className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl p-4"
              >
                <div className="flex items-start gap-2">
                  <button
                    type="button"
                    onClick={() => setModal({ kind: "studyPatients", studyDocId: s.studyDocId })}
                    className="min-w-0 flex-1 text-left"
                  >
                    <p className="text-sm font-semibold text-white truncate">
                      {s.studyId ? `${s.studyId} – ` : ""}
                      {s.name}
                    </p>
                    <p className="text-[11px] text-slate-400">
                      {s.patients.length} {s.patients.length === 1 ? "patient" : "patients"}
                      {Number(s.followUpCount) > 0 ? ` · ${Number(s.followUpCount)} follow-ups not listed` : ""} · click to
                      view or add
                    </p>
                  </button>
                  <button
                    type="button"
                    className={BTN_SMALL}
                    aria-label="Remove study from report"
                    onClick={() => {
                      if (
                        (s.patients.length === 0 && !(Number(s.followUpCount) > 0)) ||
                        window.confirm(`Remove ${s.name || "this study"} and everything you added for it from this report?`)
                      )
                        removeStudy(s.studyDocId);
                    }}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
                <div className="mt-3">
                  <TotalsChips study={s} />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* My past reports */}
        <div className="flex items-center gap-2 mb-3 px-1">
          <History className="w-3.5 h-3.5 text-purple-300" />
          <h2 className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white">
            {isAdmin ? (pastFor ? `Past reports for ${pastFor}` : "Past reports") : "My past reports"}
          </h2>
          <span className="h-px flex-1 bg-white/5" />
        </div>
        {mine.loading ? (
          <p className="text-sm text-slate-400 text-center py-4">Loading…</p>
        ) : mine.error ? (
          <p className="text-sm text-rose-300 text-center py-4">{mine.error}</p>
        ) : (
          <ReportsGrid
            reports={mine.reports}
            label="Past reports"
            showCoordinator={false}
            emptyText={
              isAdmin && !pastFor
                ? "Pick a coordinator above to see their past reports."
                : "No patient-level reports yet. Reports from before this form only had numbers and aren't listed here."
            }
          />
        )}
      </div>

      {/* Sticky bar */}
      <div className="fixed bottom-0 inset-x-0 z-20 border-t border-white/10 bg-slate-950/70 backdrop-blur-2xl">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <span className={`text-[12px] ${saveState === "error" ? "text-rose-300" : "text-slate-400"}`}>
            {saveState === "saving" && <Loader2 className="inline w-3 h-3 mr-1 animate-spin" />}
            {saveText || (blockedReason && hasWork ? blockedReason : "")}
          </span>
          <button
            type="button"
            disabled={!hasWork}
            onClick={() => setModal({ kind: "review" })}
            className={`${BTN_PRIMARY} ml-auto`}
          >
            <Send className="w-4 h-4" />
            Review &amp; submit
          </button>
        </div>
      </div>

      {/* Pop-ups */}
      <StudyPickerModal
        open={modal?.kind === "studyPicker"}
        studies={studies}
        alreadyAdded={studyIdsInReport}
        onPick={onPickStudy}
        onClose={() => setModal(null)}
      />
      <StudyPatientsModal
        open={modal?.kind === "studyPatients"}
        study={modal?.kind === "studyPatients" ? modalStudy : null}
        suggestions={suggestions}
        onFollowUpCountChange={(v) => setFollowUpCount(modal.studyDocId, v)}
        onAddPatient={() => openAddPatient(modal.studyDocId)}
        onEditPatient={(p) => {
          setPending(modal.studyDocId, { ...p }, true);
          setModal({ kind: "addPatient", studyDocId: modal.studyDocId });
        }}
        onRemovePatient={(key) => removePatient(modal.studyDocId, key)}
        onQuickAdd={(s, st) => onQuickAdd(modal.studyDocId, s, st)}
        onDone={() => setModal({ kind: "another" })}
        onClose={() => setModal(null)}
      />
      <AddPatientModal
        open={modal?.kind === "addPatient"}
        study={modal?.kind === "addPatient" ? modalStudy : null}
        entry={
          draft.pending && modal?.studyDocId && draft.pending.studyDocId === modal.studyDocId
            ? draft.pending.entry
            : null
        }
        editing={!!draft.pending?.editing}
        index={patientsState.index}
        loading={patientsState.loading}
        sheetError={patientsState.sheetError}
        onAddToRegx={() => {
          setRegxNotice("");
          setModal({ kind: "addToRegx", studyDocId: modal.studyDocId });
        }}
        notice={regxNotice}
        onChange={(entry) => setPending(modal.studyDocId, entry, !!draft.pending?.editing)}
        onAdd={(entry) => {
          upsertPatient(modal.studyDocId, entry);
          setModal({ kind: "studyPatients", studyDocId: modal.studyDocId });
        }}
        onDiscard={() => {
          clearPending();
          setModal({ kind: "studyPatients", studyDocId: modal.studyDocId });
        }}
        onClose={() => setModal({ kind: "studyPatients", studyDocId: modal.studyDocId })}
      />
      {/* "Add to RegX" from the not-in-RegX form; back to the patient after. */}
      {modal?.kind === "addToRegx" && draft.pending && draft.pending.studyDocId === modal.studyDocId && (
        <AddToRegxModal
          open
          initial={draft.pending.entry}
          reportStudyId={modal.studyDocId}
          studies={studies}
          coordinatorNames={coordinators.map((c) => c.name)}
          onCancel={() => setModal({ kind: "addPatient", studyDocId: modal.studyDocId })}
          onUseExisting={(p) => {
            const name = p.fullName || p.name || "";
            setPending(modal.studyDocId, {
              ...draft.pending.entry,
              patientRef: { source: p.source, id: p.id },
              name,
              dob: p.dob || "",
              mrn: p.mrn || "",
              notInRegx: false,
            }, !!draft.pending.editing);
            setRegxNotice("");
            setModal({ kind: "addPatient", studyDocId: modal.studyDocId });
          }}
          onAdded={({ id, record }) => {
            setPending(modal.studyDocId, {
              ...draft.pending.entry,
              patientRef: { source: "firestore", id },
              name: record.name,
              dob: record.dob,
              mrn: record.mrn,
              notInRegx: false,
            }, !!draft.pending.editing);
            // Searchable straight away.
            setPatientsState((st) => ({
              ...st,
              index: [...st.index, ...buildSearchIndex([normalizePatientForMatching({ id, ...record }, "firestore", new Date())])],
            }));
            setRegxNotice(`Added to RegX in ${statusLabel(record.status)}.`);
            setModal({ kind: "addPatient", studyDocId: modal.studyDocId });
          }}
        />
      )}
      <AnotherStudyModal
        open={modal?.kind === "another"}
        onAnother={() => setModal({ kind: "studyPicker" })}
        onFinish={() => setModal({ kind: "review" })}
        onClose={() => setModal(null)}
      />
      <ReviewSubmitModal
        open={modal?.kind === "review"}
        studies={draft.studies}
        counts={draft.counts}
        coordinator={coordinatorName}
        segment={segment}
        canSubmit={canSubmit}
        blockedReason={blockedReason}
        submitting={submitting}
        error={submitError}
        onSubmit={handleSubmit}
        onClose={() => setModal(null)}
      />
      <LeaveGuardModal
        open={modal?.kind === "leave"}
        saveFailed={saveState === "error"}
        onStay={() => setModal(null)}
        onLeave={() => navigate(-1)}
      />
    </div>
  );
};

export default WeeklySurvey;
